import { Suspense, use } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { FeatureItem } from '~/components/feature-item'
import {
  AllowlistIcon,
  CustomPhraseIcon,
  DailyChallengeBonusIcon,
  FavoriteSettingsIcon,
  NoAdsIcon,
  PauseIcon,
  RealTreeIcon,
  SeasonalTreeIcon,
  StatsIcon,
  TagIcon,
  TimeGuardIcon,
  TreesIcon,
  TripleCoinsIcon,
} from '~/components/icons'
import { Spinner } from '~/components/ui/spinner'
import { regionReady, serviceLevelStatusReady } from '~/loading'
import { m } from '~/paraglide/messages'
import { enterFrom } from '~/params'

export function FeatureList() {
  return (
    <div className="w-full rounded-[21px] bg-[rgba(0,161,232,0.1)] bg-gradient-to-b from-[#03485E] to-[#002536] p-[3px]">
      <div className="flex flex-col gap-5 rounded-[18px] bg-gradient-to-b from-[#013952] to-[#022636] px-[21px] py-[21px]">
        <ErrorBoundary fallback={<></>}>
          <Suspense
            fallback={
              <div className="grid h-32 place-items-center">
                <Spinner className="size-9" />
              </div>
            }
          >
            <Content />
          </Suspense>
        </ErrorBoundary>
      </div>
    </div>
  )
}

function Content() {
  const region = use(regionReady.promise)
  const serviceLevelStatus = use(serviceLevelStatusReady.promise)
  const features = getFeatures(region ?? '', serviceLevelStatus.is_premium)
  return features.map((feature) => (
    <FeatureItem
      key={feature.message}
      icon={feature.Icon}
      text={feature.message}
    />
  ))
}

type Feature = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  message: string
}

function getFeatures(region: string, isProUser: boolean): Feature[] {
  // focus-challenge enters the paywall to upsell its Plus daily challenge, so it
  // leads with a bonus feature for that challenge (sprint plan §5.2.1).
  const dailyChallengeBonus: Feature = {
    Icon: DailyChallengeBonusIcon,
    message: m.plus_bonus_daily_challenge(),
  }

  if (isProUser) {
    const baseFeatures = [
      {
        Icon: SeasonalTreeIcon,
        message: m.early_access_to_plus_exlusive_tree(),
      },
      { Icon: TreesIcon, message: m.seasonal_crystal_trees() },
      { Icon: TripleCoinsIcon, message: m.triple_coin_reward() },
      { Icon: TimeGuardIcon, message: m.app_block() },
    ]

    switch (enterFrom) {
      case 'time_guard_edit_plan':
      case 'time_guard_select_apps':
        return moveToFirst(baseFeatures, TimeGuardIcon)
      case 'focus_challenge':
        return [
          dailyChallengeBonus,
          ...moveToFirst(baseFeatures, TimeGuardIcon),
        ]
      default:
        // this should never happen
        return baseFeatures
    }
  }

  const baseFeatures: Feature[] =
    region.toLowerCase() === 'cn'
      ? [
          { Icon: AllowlistIcon, message: m.custom_app_allow_list() },
          { Icon: NoAdsIcon, message: m.no_ads() },
          { Icon: SeasonalTreeIcon, message: m.free_use_crystal_tree() },
          { Icon: TripleCoinsIcon, message: m.triple_coin_reward() },
          { Icon: StatsIcon, message: m.focus_trend_analysis() },
          { Icon: TimeGuardIcon, message: m.app_block() },
        ]
      : [
          { Icon: AllowlistIcon, message: m.custom_app_allow_list() },
          { Icon: SeasonalTreeIcon, message: m.free_use_crystal_tree() },
          { Icon: StatsIcon, message: m.focus_trend_analysis() },
          { Icon: RealTreeIcon, message: m.plant_tree_for_earth() },
          { Icon: TripleCoinsIcon, message: m.triple_coin_reward() },
          { Icon: TimeGuardIcon, message: m.app_block() },
        ]

  switch (enterFrom) {
    case 'focus_challenge':
      // Free users: lead with the daily-challenge bonus, drop app block.
      return prependAndRemoveTimeGuard(baseFeatures, dailyChallengeBonus)
    case 'statistics':
      return moveToFirst(baseFeatures, StatsIcon)
    case 'tag_list_edit':
    case 'tag_list_create':
    case 'focus_settings_create_tag':
      return prependAndRemoveTimeGuard(baseFeatures, {
        Icon: TagIcon,
        message: m.subscription_exclusive_feature_tag_title(),
      })
    case 'focus_settings_favorites':
      return prependAndRemoveTimeGuard(baseFeatures, {
        Icon: FavoriteSettingsIcon,
        message: m.plant_set_tab_2(),
      })
    case 'time_guard_edit_plan':
    case 'time_guard_select_apps':
      return moveToFirst(baseFeatures, TimeGuardIcon)
    case 'settings_custom_phrase':
      return prependAndRemoveTimeGuard(baseFeatures, {
        Icon: CustomPhraseIcon,
        message: m.premium_more_feature1_title(),
      })
    case 'pause_cover':
    case 'planting_pause':
      return prependAndRemoveTimeGuard(baseFeatures, {
        Icon: PauseIcon,
        message: m.unlimited_pauses(),
      })
    default:
      return baseFeatures
  }
}

function moveToFirst(features: Feature[], Icon: Feature['Icon']): Feature[] {
  const target = features.find((f) => f.Icon === Icon)
  if (!target) return features
  return [target, ...features.filter((f) => f.Icon !== Icon)]
}

function prependAndRemoveTimeGuard(
  features: Feature[],
  newFeature: Feature,
): Feature[] {
  return [newFeature, ...features.filter((f) => f.Icon !== TimeGuardIcon)]
}
